import {
  DateInput,
  SelectOptionType,
  Table,
  TextInput,
} from '@octano/global-ui';
import { CellFormatOptions } from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import ChipMultiSelect from '../../../../../../components/form/ChipMultiSelect';
import SectionTitle from '../../../../../../components/text/SectionTitle';

const prefix = 'tariffAndTuition.discounts';
const discountPrefix = `${prefix}.discount`;

interface SelectOptions {
  academicLevels: SelectOptionType[];
  periods: SelectOptionType[];
  studyPlans: SelectOptionType[];
  campuses: SelectOptionType[];
  schedules: SelectOptionType[];
  modalities: SelectOptionType[];
}

interface FormValues {
  hasAutomaticAssignment: boolean;
  minimumGradeAverage: string;
  continuidadAcademicLevelIds: SelectOptionType[];
  continuidadStudyPlanIds: SelectOptionType[];
  continuidadCampusIds: SelectOptionType[];
  continuidadScheduleIds: SelectOptionType[];
  continuidadModalityIds: SelectOptionType[];
  continuidadPeriodIds: SelectOptionType[];
  enrollmentDates: { periodId: number; startDate: string; endDate: string }[];
  [key: string]: any;
}

interface DiscountContinuidadSectionProps {
  selectOptions: SelectOptions;
  getPeriodName: (periodId: number) => string;
}

const DiscountContinuidadSection = ({
  selectOptions,
  getPeriodName,
}: DiscountContinuidadSectionProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<FormValues>();

  const hasAutomaticAssignment = useWatch({
    control,
    name: 'hasAutomaticAssignment',
  });

  const continuidadPeriodIds = useWatch({
    control,
    name: 'continuidadPeriodIds',
  });

  const enrollmentDates =
    useWatch({
      control,
      name: 'enrollmentDates',
    }) || [];

  if (!hasAutomaticAssignment) return null;

  return (
    <>
      <Row className="mb-3 mt-5">
        <Col xs="12">
          <SectionTitle text={t(`${discountPrefix}.continuidadParamsTitle`)} />
        </Col>
      </Row>

      <Row>
        <Col xs="12" md="6" className="mb-3">
          <ChipMultiSelect
            name="continuidadAcademicLevelIds"
            label={t(`${discountPrefix}.academicLevel`)}
            options={selectOptions.academicLevels}
            control={control}
            placeholder={t(`${discountPrefix}.selectOption`)}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ChipMultiSelect
            name="continuidadStudyPlanIds"
            label={t(`${discountPrefix}.studyPlan`)}
            options={selectOptions.studyPlans}
            control={control}
            placeholder={t(`${discountPrefix}.selectOption`)}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ChipMultiSelect
            name="continuidadCampusIds"
            label={t(`${discountPrefix}.campus`)}
            options={selectOptions.campuses}
            control={control}
            placeholder={t(`${discountPrefix}.selectOption`)}
          />
        </Col>

        <Col xs="12" md="6" className="mb-3">
          <ChipMultiSelect
            name="continuidadScheduleIds"
            label={t(`${discountPrefix}.schedule`)}
            options={selectOptions.schedules}
            control={control}
            placeholder={t(`${discountPrefix}.selectOption`)}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ChipMultiSelect
            name="continuidadModalityIds"
            label={t(`${discountPrefix}.modality`)}
            options={selectOptions.modalities}
            control={control}
            placeholder={t(`${discountPrefix}.selectOption`)}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <div className="d-flex align-items-end">
            <div className="flex-grow-1">
              <TextInput
                name="minimumGradeAverage"
                control={control}
                label={t(`${discountPrefix}.minimumGradeAverage`)}
                formatter={(value: string) => {
                  const num = value.replace(/[^\d.]/g, '');
                  const parsed = parseFloat(num);
                  if (isNaN(parsed) || parsed < 0 || parsed > 10) return '';
                  return num;
                }}
                tooltip={t(`${discountPrefix}.minimumGradeAverageTooltip`)}
              />
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs="12" md="6" className="mb-3">
          <ChipMultiSelect
            name="continuidadPeriodIds"
            label={t(`${discountPrefix}.enrollmentPeriod`)}
            options={selectOptions.periods}
            control={control}
            placeholder={t(`${discountPrefix}.selectOption`)}
          />
        </Col>
      </Row>

      {continuidadPeriodIds && continuidadPeriodIds.length > 0 && (
        <>
          <Row className="mb-2 mt-4">
            <Col xs="12">
              <p className="text-muted">
                {t(`${discountPrefix}.enrollmentDatesTitle`)}
              </p>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col id="curriculum-mention-list-table-2" xs="12">
              <Table
                columns={[
                  {
                    columnName: 'periodName',
                    headerText: t(`${discountPrefix}.periodName`),
                  },
                  {
                    columnName: 'startDate',
                    headerText: t(`${discountPrefix}.dateFrom`),
                    cellFormat: (options: CellFormatOptions) => {
                      const idx = options.index;
                      return (
                        <DateInput
                          name={`enrollmentStartDate_${idx}`}
                          label=""
                          value={enrollmentDates[idx]?.startDate || ''}
                          onChange={(value: any) => {
                            const current = [...enrollmentDates];
                            current[idx] = {
                              ...current[idx],
                              startDate: value,
                            };
                            setValue('enrollmentDates', current, {
                              shouldDirty: true,
                            });
                          }}
                        />
                      );
                    },
                  },
                  {
                    columnName: 'endDate',
                    headerText: t(`${discountPrefix}.dateTo`),
                    cellFormat: (options: CellFormatOptions) => {
                      const idx = options.index;
                      const startDateValue = enrollmentDates[idx]?.startDate;
                      const minDate = startDateValue
                        ? new Date(startDateValue)
                        : undefined;
                      return (
                        <DateInput
                          name={`enrollmentEndDate_${idx}`}
                          label=""
                          value={enrollmentDates[idx]?.endDate || ''}
                          minDate={minDate}
                          onChange={(value: any) => {
                            const current = [...enrollmentDates];
                            current[idx] = {
                              ...current[idx],
                              endDate: value,
                            };
                            setValue('enrollmentDates', current, {
                              shouldDirty: true,
                            });
                          }}
                        />
                      );
                    },
                  },
                ]}
                data={continuidadPeriodIds.map((period: SelectOptionType) => ({
                  periodName: getPeriodName(period.value as number),
                  startDate: '',
                  endDate: '',
                }))}
                noResultsText=""
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default DiscountContinuidadSection;
